import {
    Component,
    inject,
    input,
    OnInit,
    signal,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    DestroyRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterLink, RouterLinkActive } from '@angular/router'
import { AuthStateService } from '@baboor/baboor-auth'
import { SpanishDirective } from '@baboor/baboor-language'
import { PERMISSIONS } from '@baboor/baboor-models'
import { AppDetailsService } from '@baboor/common-services'
import {
    provideTranslocoScope,
    TranslocoModule,
    TranslocoService,
} from '@jsverse/transloco'
import { TooltipModule } from '@openng/optimus-ui/tooltip'

export interface NavigationItem {
    label: string
    icon: string
    routerLink: string
    permission?: string
}

@Component({
    selector: 'app-sidebar-easy-mode',
    imports: [
        RouterLink,
        RouterLinkActive,
        TranslocoModule,
        SpanishDirective,
        TooltipModule,
    ],
    templateUrl: './sidebar-easy-mode.component.html',
    styleUrl: './sidebar-easy-mode.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [provideTranslocoScope('vendorDashboard')],
})
export class SidebarEasyModeComponent implements OnInit {
    authStateService = inject(AuthStateService)
    private appDetailsService = inject(AppDetailsService)
    private cdr = inject(ChangeDetectorRef)
    private destroyRef = inject(DestroyRef)
    isCollapsed = input(false)
    version = signal<string>('')

    navigationItems: NavigationItem[] = [
        {
            label: 'vendorDashboard.easyMode.sidebarEasyMode.home',
            routerLink: '/easy/dashboard/home',
            icon: 'pi-home',
        },
        {
            label: 'vendorDashboard.easyMode.sidebarEasyMode.appointments',
            routerLink: '/easy/dashboard/appointments',
            icon: 'pi-calendar',
            permission: PERMISSIONS.appointment.read,
        },
        {
            label: 'vendorDashboard.easyMode.sidebarEasyMode.calendar',
            routerLink: '/easy/dashboard/calendar',
            icon: 'pi-calendar',
            permission: PERMISSIONS.calendar.read,
        },
        {
            label: 'vendorDashboard.easyMode.sidebarEasyMode.clients',
            routerLink: '/easy/dashboard/clients',
            icon: 'pi-users',
            permission: PERMISSIONS.crm.read,
        },
        {
            label: 'vendorDashboard.easyMode.sidebarEasyMode.organization',
            routerLink: '/easy/dashboard/organization',
            icon: 'pi-building',
            permission: PERMISSIONS.organization.read,
        },
    ]
    filteredNavigationItems: NavigationItem[] = []

    constructor() {
        this.updateNavigation()
    }

    async ngOnInit() {
        this.updateNavigation()

        this.authStateService
            .select('userDetails')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.updateNavigation()
            })

        const appVersion = await (
            await this.appDetailsService.getAppDetails()
        ).version
        this.version.set(appVersion)
    }

    private updateNavigation() {
        this.filteredNavigationItems = this.navigationItems.filter(
            (item) =>
                !item.permission ||
                this.authStateService.hasPermission(item.permission),
        )
        this.cdr.markForCheck()
    }
}
